import type { Session } from "@supabase/supabase-js";
import { authClient } from "./auth";
import { withBase } from "./base";
import type { Slot } from "./schedule";

// Standing classes, as the Worker's /api/my-classes reports them, and the few
// writes a teacher makes to them. Writes go straight to Supabase under the
// signed-in session; row-level security is what stops anyone writing another
// teacher's class.

export type ResumePoint = { stageType: string; stageId: string; ayah: number; page: number; endedAt: string };
export type RoomStatus = { open: boolean; participants: number; resume: ResumePoint | null };
export type ClassMember = { id: string; name: string; role: string; joinedAt: string };
export type QuranClass = {
  id: string;
  title: string;
  teacherName: string;
  roomCode: string;
  timezone: string;
  slots: Slot[];
  durationMinutes: number;
  inviteToken?: string;
  members?: ClassMember[];
  status: RoomStatus;
};
export type MyClasses = { available: boolean; teaching: QuranClass[]; learning: QuranClass[]; children?: Array<{ id: string; name: string }> };

/** A learner's record as their teacher or parent may see it. */
export type StudentJourney = {
  profile: { name?: string } | null;
  progress: Partial<import("../types").LearnerProgress> | null;
  lessons: Array<{ endedAt: string; minutes: number }>;
};

export async function fetchStudentJourney(session: Session, studentId: string): Promise<StudentJourney> {
  const response = await fetch(withBase(`/api/students/${encodeURIComponent(studentId)}/journey`), {
    headers: { Authorization: `Bearer ${session.access_token}` },
  });
  const body = await response.json().catch(() => ({})) as Partial<StudentJourney> & { error?: string };
  if (!response.ok) throw new Error(body.error || "This learner's progress could not be loaded.");
  return { profile: body.profile ?? null, progress: body.progress ?? null, lessons: body.lessons ?? [] };
}

export async function fetchMyClasses(session: Session): Promise<MyClasses | null> {
  try {
    const response = await fetch(withBase("/api/my-classes"), { headers: { Authorization: `Bearer ${session.access_token}` } });
    if (!response.ok) return null;
    return await response.json() as MyClasses;
  } catch {
    return null;
  }
}

/** Ten characters a parent can read aloud without "is that an l or a 1?". */
export function newRoomCode() {
  const alphabet = "abcdefghjkmnpqrstuvwxyz23456789";
  const bytes = crypto.getRandomValues(new Uint8Array(10));
  return `noor-${[...bytes].map((byte) => alphabet[byte % alphabet.length]).join("")}`;
}

function newInviteToken() {
  return [...crypto.getRandomValues(new Uint8Array(16))].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

export type ClassInput = { title: string; timezone: string; slots: Slot[]; durationMinutes: number };

function database() {
  const client = authClient();
  if (!client) throw new Error("Sign in with your Al Noor Tuition account first.");
  return client;
}

function friendly(error: { message: string; code?: string }) {
  if (error.code === "42501" || /row-level security/i.test(error.message)) return new Error("Only a teacher account can set up a class.");
  if (error.code === "42P01" || error.code === "PGRST205") return new Error("Classes are not switched on yet. Please try again later.");
  return new Error(error.message);
}

export async function createClass(teacher: { id: string; name: string }, input: ClassInput) {
  // A clash on the room code is astronomically unlikely, but costs nothing to retry.
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const { error } = await database().from("quran_classes").insert({
      teacher_id: teacher.id,
      teacher_name: teacher.name.slice(0, 80),
      title: input.title.trim().slice(0, 80),
      room_code: newRoomCode(),
      timezone: input.timezone,
      slots: input.slots,
      duration_minutes: input.durationMinutes,
    });
    if (!error) return;
    if (error.code !== "23505") throw friendly(error);
  }
  throw new Error("The class could not be created. Please try again.");
}

export async function updateClass(id: string, input: ClassInput) {
  const { error } = await database().from("quran_classes").update({
    title: input.title.trim().slice(0, 80),
    timezone: input.timezone,
    slots: input.slots,
    duration_minutes: input.durationMinutes,
    updated_at: new Date().toISOString(),
  }).eq("id", id);
  if (error) throw friendly(error);
}

export async function archiveClass(id: string) {
  const { error } = await database().from("quran_classes").update({ archived_at: new Date().toISOString() }).eq("id", id);
  if (error) throw friendly(error);
}

export async function removeMember(classId: string, memberId: string) {
  const { error } = await database().from("quran_class_members")
    .update({ removed_at: new Date().toISOString() }).eq("class_id", classId).eq("member_id", memberId);
  if (error) throw friendly(error);
}

/** A new invite link. The old one stops working at once. */
export async function rotateInvite(classId: string) {
  const { error } = await database().from("quran_classes").update({ invite_token: newInviteToken() }).eq("id", classId);
  if (error) throw friendly(error);
}

export async function joinByInvite(token: string): Promise<{ title: string; roomCode: string }> {
  const { data, error } = await database().rpc("quran_join_class", { p_token: token });
  if (error) throw friendly(error);
  const row = (Array.isArray(data) ? data[0] : data) as { title?: string; room_code?: string } | null;
  return { title: row?.title ?? "", roomCode: row?.room_code ?? "" };
}

export function inviteLink(token: string) {
  return `${window.location.origin}${withBase("/")}?invite=${encodeURIComponent(token)}#classroom`;
}

/** A 32-hex invite token from the address bar, or null. */
export function inviteFromLocation(search: string): string | null {
  const token = new URLSearchParams(search).get("invite")?.trim().toLowerCase() ?? "";
  return /^[a-f0-9]{32}$/.test(token) ? token : null;
}
